import { getViewProps } from '../../viewHandler'
import { getVeloConstantMaybe } from '../shared/lib/veloConstants'
import i18n, { isLanguageValid } from './config'
import { Language } from './types'

const preloads = new Map<string, Promise<void>>()

/**
 * The locale is fixed per page load: main pages set `<html lang>`, embed
 * pages pass the UI locale via VIEW_PROPS (their Gettext `currentLocale`
 * is the content locale and can differ), the renderer sets neither and
 * stays English.
 */
export function resolveActiveLocale(): string | undefined {
  const viewProps = getViewProps() as { locale?: string } | undefined

  return document.documentElement.lang || viewProps?.locale || getVeloConstantMaybe('currentLocale')
}

/**
 * Fetches the merged translation bundle for the given locale (built by
 * `mix assets.locales`) and registers it with i18next. English is already
 * bundled, so it resolves immediately. Must complete before views mount —
 * react-i18next does not re-render on late `addResourceBundle` calls.
 */
export function preloadLocaleResources(lang: string | undefined): Promise<void> {
  if (!lang || !isLanguageValid(lang) || lang === 'en') {
    return Promise.resolve()
  }

  const existing = preloads.get(lang)
  if (existing) {
    return existing
  }

  const promise = fetchLocaleResources(lang)
  preloads.set(lang, promise)
  return promise
}

async function fetchLocaleResources(lang: Exclude<Language, 'en'>): Promise<void> {
  try {
    const url = getVeloConstantMaybe(`i18nLocaleUrls.${lang}`)
    if (!url) {
      return
    }

    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Locale bundle fetch failed with status ${response.status}`)
    }

    const data: Record<string, object> = await response.json()
    for (const [namespace, bundle] of Object.entries(data)) {
      i18n.addResourceBundle(lang, namespace, bundle, true, true)
    }
  } catch (error) {
    console.error('[i18n] Failed to load locale resources, falling back to English', error)
  }
}
